import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { SignalrMessageAdapter } from '../eventhub/signalr-message-adapter';

/** Which connection notice the shell shows: none while everything works. */
export type ConnectionBanner = 'offline' | 'realtime' | 'recovered' | null;

/** How long "connection restored" stays before it fades out. */
export const RECOVERED_VISIBLE_MS = 4000;

/**
 * What the user needs to know about the connection:
 * - offline: the browser reports no network, nothing is saved until it is back;
 * - realtime: the live connection (SignalR) gave up for good, so lists no longer update on their own;
 * - recovered: after either problem went away, briefly.
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  private readonly signalr = inject(SignalrMessageAdapter);

  private readonly _online = signal(typeof navigator === 'undefined' ? true : navigator.onLine);
  readonly online = this._online.asReadonly();

  private readonly _recovered = signal(false);
  private readonly _dismissed = signal<ConnectionBanner>(null);
  private hadProblem = false;
  private recoveredTimer?: ReturnType<typeof setTimeout>;

  private readonly current = computed<ConnectionBanner>(() => {
    if (!this._online()) return 'offline';
    if (this.signalr.connectionState() === 'closed') return 'realtime';
    return this._recovered() ? 'recovered' : null;
  });

  /** The notice to show; null when there is none or the user closed it. */
  readonly banner = computed<ConnectionBanner>(() => {
    const kind = this.current();
    return kind !== null && kind === this._dismissed() ? null : kind;
  });

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this._online.set(true));
      window.addEventListener('offline', () => this._online.set(false));
    }

    effect(() => {
      const state = this.signalr.connectionState();
      const problem = !this._online() || state === 'closed' || state === 'reconnecting';
      if (problem) {
        this.hadProblem = true;
        this.clearRecovered();
        return;
      }
      // Everything works again after a problem: say so for a moment.
      if (this.hadProblem) {
        this.hadProblem = false;
        this._recovered.set(true);
        this.recoveredTimer = setTimeout(() => this.clearRecovered(), RECOVERED_VISIBLE_MS);
      }
    });

    // A closed notice comes back if the same problem returns later.
    effect(() => {
      if (this.current() === null) this._dismissed.set(null);
    });
  }

  /** The user closed the notice (not offered for "offline", which clears itself). */
  dismiss(): void {
    this._dismissed.set(this.current());
  }

  private clearRecovered(): void {
    clearTimeout(this.recoveredTimer);
    this._recovered.set(false);
  }
}
